/**
 * Certificate utility functions for SSO certificate management.
 * Handles PEM formatting, file download, and X.509 expiry date parsing.
 */

/**
 * Formats a raw base64 certificate string into PEM format.
 * Inserts line breaks at 64-character intervals and wraps with PEM headers.
 */
export function formatPemCertificate(base64Cert: string): string {
  const lines: string[] = [];
  for (let i = 0; i < base64Cert.length; i += 64) {
    lines.push(base64Cert.slice(i, i + 64));
  }
  return [
    "-----BEGIN CERTIFICATE-----",
    ...lines,
    "-----END CERTIFICATE-----",
  ].join("\n");
}

/**
 * Generates a certificate download filename based on the IdP name and certificate type.
 * @param idpName - The Identity Provider name
 * @param type - The certificate type ("signing" or "encryption")
 * @returns A filename in the format "{idpName}.{type}.crt"
 */
export function getCertificateFileName(
  idpName: string,
  type: "signing" | "encryption",
): string {
  return `${idpName}.${type}.crt`;
}

/**
 * Triggers a file download with the given PEM content.
 * Creates a Blob with MIME type application/x-pem-file, generates an object URL,
 * triggers download via a programmatic <a> click, then revokes the URL.
 */
export function downloadCertificate(
  pemContent: string,
  fileName: string,
): void {
  const blob = new Blob([pemContent], { type: "application/x-pem-file" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Parses the notAfter date from a DER-encoded X.509 certificate.
 * Uses lightweight ASN.1 parsing — no external dependencies.
 *
 * Strategy:
 * 1. Base64-decode the certificate to a Uint8Array
 * 2. Parse the outer SEQUENCE tag (0x30)
 * 3. Navigate to TBSCertificate (first element in the outer SEQUENCE)
 * 4. Inside TBSCertificate, skip: version (context tag [0]), serialNumber,
 *    signature algorithm, issuer
 * 5. Parse the Validity SEQUENCE containing notBefore and notAfter
 * 6. Decode the notAfter field (UTCTime or GeneralizedTime)
 *
 * Handles:
 * - UTCTime (tag 0x17): format YYMMDDHHMMSSZ
 *   Years 00-49 → 2000-2049, years 50-99 → 1950-1999
 * - GeneralizedTime (tag 0x18): format YYYYMMDDHHMMSSZ
 *
 * Returns null if parsing fails for any reason.
 */
export function parseCertificateExpiry(base64Cert: string): Date | null {
  try {
    const der = base64ToUint8Array(base64Cert);
    let offset = 0;

    // Parse outer SEQUENCE
    const outer = parseTag(der, offset);
    if (outer === null || outer.tag !== 0x30) return null;
    offset = outer.contentOffset;

    // Parse TBSCertificate SEQUENCE
    const tbs = parseTag(der, offset);
    if (tbs === null || tbs.tag !== 0x30) return null;
    let tbsOffset = tbs.contentOffset;

    // Skip version (context tag [0], explicit) — optional field
    const maybeVersion = parseTag(der, tbsOffset);
    if (maybeVersion === null) return null;
    if (maybeVersion.tag === 0xa0) {
      // version is present, skip it
      tbsOffset = maybeVersion.contentOffset + maybeVersion.contentLength;
    }

    // Skip serialNumber (INTEGER)
    const serial = parseTag(der, tbsOffset);
    if (serial === null) return null;
    tbsOffset = serial.contentOffset + serial.contentLength;

    // Skip signature algorithm (SEQUENCE)
    const sigAlg = parseTag(der, tbsOffset);
    if (sigAlg === null) return null;
    tbsOffset = sigAlg.contentOffset + sigAlg.contentLength;

    // Skip issuer (SEQUENCE)
    const issuer = parseTag(der, tbsOffset);
    if (issuer === null) return null;
    tbsOffset = issuer.contentOffset + issuer.contentLength;

    // Parse Validity SEQUENCE
    const validity = parseTag(der, tbsOffset);
    if (validity === null || validity.tag !== 0x30) return null;
    let validityOffset = validity.contentOffset;

    // Skip notBefore
    const notBefore = parseTag(der, validityOffset);
    if (notBefore === null) return null;
    validityOffset = notBefore.contentOffset + notBefore.contentLength;

    // Parse notAfter
    const notAfter = parseTag(der, validityOffset);
    if (notAfter === null) return null;

    return parseTime(der, notAfter);
  } catch {
    return null;
  }
}

// ── Internal helpers ─────────────────────────────────────────────

interface TagInfo {
  tag: number;
  contentOffset: number;
  contentLength: number;
}

/**
 * Parses a DER tag and length at the given offset.
 * Returns the tag byte, the offset where content starts, and the content length.
 */
function parseTag(der: Uint8Array, offset: number): TagInfo | null {
  if (offset >= der.length) return null;

  const tag = der[offset];
  offset++;

  if (offset >= der.length) return null;

  let length = der[offset];
  offset++;

  if (length & 0x80) {
    // Long form length
    const numBytes = length & 0x7f;
    if (numBytes === 0 || numBytes > 4) return null;
    if (offset + numBytes > der.length) return null;

    length = 0;
    for (let i = 0; i < numBytes; i++) {
      length = (length << 8) | der[offset];
      offset++;
    }
  }

  return { tag, contentOffset: offset, contentLength: length };
}

/**
 * Parses a UTCTime or GeneralizedTime value from DER bytes.
 */
function parseTime(der: Uint8Array, tagInfo: TagInfo): Date | null {
  const { tag, contentOffset, contentLength } = tagInfo;
  const bytes = der.slice(contentOffset, contentOffset + contentLength);
  const timeStr = new TextDecoder().decode(bytes);

  if (tag === 0x17) {
    // UTCTime: YYMMDDHHMMSSZ
    return parseUtcTime(timeStr);
  } else if (tag === 0x18) {
    // GeneralizedTime: YYYYMMDDHHMMSSZ
    return parseGeneralizedTime(timeStr);
  }

  return null;
}

/**
 * Parses UTCTime format: YYMMDDHHMMSSZ
 * Years 00-49 → 2000-2049, years 50-99 → 1950-1999 (per RFC 5280)
 */
function parseUtcTime(timeStr: string): Date | null {
  if (timeStr.length < 13) return null;

  let year = parseInt(timeStr.slice(0, 2), 10);
  const month = parseInt(timeStr.slice(2, 4), 10);
  const day = parseInt(timeStr.slice(4, 6), 10);
  const hour = parseInt(timeStr.slice(6, 8), 10);
  const minute = parseInt(timeStr.slice(8, 10), 10);
  const second = parseInt(timeStr.slice(10, 12), 10);

  if (
    isNaN(year) ||
    isNaN(month) ||
    isNaN(day) ||
    isNaN(hour) ||
    isNaN(minute) ||
    isNaN(second)
  ) {
    return null;
  }

  // RFC 5280: years 00-49 → 2000-2049, years 50-99 → 1950-1999
  year = year >= 50 ? 1900 + year : 2000 + year;

  return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
}

/**
 * Parses GeneralizedTime format: YYYYMMDDHHMMSSZ
 */
function parseGeneralizedTime(timeStr: string): Date | null {
  if (timeStr.length < 15) return null;

  const year = parseInt(timeStr.slice(0, 4), 10);
  const month = parseInt(timeStr.slice(4, 6), 10);
  const day = parseInt(timeStr.slice(6, 8), 10);
  const hour = parseInt(timeStr.slice(8, 10), 10);
  const minute = parseInt(timeStr.slice(10, 12), 10);
  const second = parseInt(timeStr.slice(12, 14), 10);

  if (
    isNaN(year) ||
    isNaN(month) ||
    isNaN(day) ||
    isNaN(hour) ||
    isNaN(minute) ||
    isNaN(second)
  ) {
    return null;
  }

  return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
}

/**
 * Decodes a base64 string to a Uint8Array.
 */
function base64ToUint8Array(base64: string): Uint8Array {
  const binaryStr = atob(base64);
  const bytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  return bytes;
}
